<template>
  <div
    class="file-upload"
    :class="{
      'file-upload--invalid': !isValid || isForceInvalid,
      'file-upload--required': required,
    }"
  >
    <!-- Label is auto-generated from allowed file size and file types-->
    <label class="file-upload__label" :for="uniqueId">{{ label }}</label>
    <label class="file-upload__wrapper" :for="uniqueId">
      <input
        :id="uniqueId"
        class="file-upload__input"
        type="file"
        :name="name"
        :required="required"
        :accept="accept"
        :data-tosca="getToscaPrefix('input', toscaPrefix)"
        :aria-invalid="!isValid"
        :aria-required="required"
        :aria-describedby="errorMessage || legend ? `${uniqueId}-legend` : undefined"
        @change="handleFileChange($event)"
      />
      <span
        class="file-upload__input-text"
        :data-text="placeholderText"
        :data-select="$t('form.input.file.button')"
      ></span>
    </label>

    <div :id="`${uniqueId}-legend`" class="file-upload__legend">
      <span v-if="legend">
        {{ legend }}
      </span>

      <span aria-live="polite">
        <span
          v-if="!isValid"
          class="file-upload__error-message"
          :data-tosca="getToscaPrefix('error-msg', toscaPrefix)"
        >
          {{ errorMessage }}
        </span>
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import getId from "~/composables/getId/getId";
import useI18n from "~/composables/i18n/useI18n";
import type { InputInterface } from "~/composables/inputs/useInputValues.types";
import { getToscaPrefix } from "~/utils/ui";
import type { SparFileUploadProps } from "./SparFileUpload.types";

const props: SparFileUploadProps = defineProps({
  label: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    default: null,
  },
  // Byte
  maxSize: {
    type: Number,
    required: true,
  },
  acceptedFileExtensions: {
    type: Array as PropType<string[]>,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  toscaPrefix: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(["interface"]);
const uniqueId = getId("file");
const { $t } = useI18n();

const isValid = ref(true); // Internal Validation
const isForceInvalid = ref(false); // External Validation
const file: Ref<File | null> = ref(null);
const errorMessage = ref("");

const legend = computed(() => {
  const res: string[] = [];

  res.push(
    $t("form.input.file.legend.size", {
      size: maxFileSizeMB.value.toString(),
    }),
  );

  res.push($t("form.input.file.legend.types") + ": " + accept.value);
  return res.join(" ");
});

const handleFileChange = (e: Event) => {
  reset();
  const target = e.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    file.value = target.files[0];
  }
  validate();
};

const accept = computed(() => {
  return props.acceptedFileExtensions.map((ext) => "." + ext).join(", ");
});

const maxFileSizeMB = computed(() => {
  return Math.round(props.maxSize / 1024 / 1024);
});

const placeholderText = computed(() => {
  if (file.value) {
    return file.value.name;
  }
  return $t("form.input.file.placeholder");
});

const validate = () => {
  errorMessage.value = "";
  if (props.required && !file.value) {
    errorMessage.value = $t("form.validation.field_required");
    isValid.value = false;
  } else if (!file.value) {
    isValid.value = true;
  } else {
    const fileExtention = file.value.name.split(".").pop();
    const isValidType = Boolean(
      fileExtention && props.acceptedFileExtensions.includes(fileExtention),
    );
    if (!isValidType) {
      errorMessage.value =
        $t("form.input.file.error.type") +
        " " +
        $t("form.input.file.legend.types") +
        ": " +
        accept.value;
    }

    const isValidFileSize = file.value.size <= props.maxSize;
    if (!isValidFileSize) {
      errorMessage.value = $t("form.input.file.error.size", {
        size: maxFileSizeMB.value.toString(),
      });
    }

    isValid.value = isValidType && isValidFileSize;
  }
  return isValid.value;
};

const getValue = () => {
  return "TODO";
};

const getProps = () => {
  return props;
};

const reset = () => {
  file.value = null;
  errorMessage.value = "";
  isValid.value = true;
  isForceInvalid.value = false;
};

const setForceInvalid = (invalid = true) => {
  isForceInvalid.value = invalid;
};

const emitInterface = () => {
  emit("interface", {
    uniqueId,
    validate: () => validate(),
    reset: () => reset(),
    setForceInvalid,
    getValue: () => getValue(),
    getProps: () => getProps(),
  } as InputInterface);
};

onMounted(() => {
  emitInterface();
});
</script>

<style lang="scss">
@use "./SparFileUpload.scss";
</style>
